import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild} from '@angular/core';
import {SearchForm, SearchFormOptionItem} from "../../../Model/Dto/SearchForm";
import {SearchFormsService} from "../../../Services/SearchForms/searchforms.service";
import {LoginService} from "../../../Services/login.service";
import {DxPopoverComponent} from "devextreme-angular";
import {TranslateService} from "@ngx-translate/core";
import {UpdateTemplateService} from "../../../Services/updateTemplate.service";
import {TemplateFieldsService} from "../../../Services/templateFields.service";
import {TemplateItemTemplate} from "../../../Model/Catalog/TemplateItemTemplate";
import {NextPimField} from "../../../Model/Catalog/NextPimField";
import {ValidFeatureService} from "../../../Services/validFeature.service";
import {ValidFeatureSystem} from "../../../Model/ValidFeatureSystem";
import DataSource from "devextreme/data/data_source";
import ArrayStore from "devextreme/data/array_store";
import {lastValueFrom} from "rxjs";
import {HttpErrorResponse} from "@angular/common/http";
import {SystemService} from "../../../Services/system.service";

@Component({
  selector: 'np-search-forms',
  standalone: false,
  templateUrl: './search-forms.component.html',
  styleUrl: './search-forms.component.css'
})
export class SearchFormsComponent implements OnChanges {

  @Input() initialSearchForm: SearchForm;
  @Output() saved = new EventEmitter<SearchForm>();
  @Output() cancelled = new EventEmitter<void>();
  @ViewChild('addSearchFormPopup', {static: false}) popoverInstance: DxPopoverComponent;

  allSearchForms: SearchForm[];
  selectedListItem: SearchForm | null = null;
  selectedSearchForm: SearchForm;
  groups: string[] = [];
  allFields: TemplateItemTemplate[] = [];
  private groupFieldsCache: Map<string, TemplateItemTemplate[]> = new Map();
  conditionValue: 'and' | 'or';
  createNewSearchFormPopoverVisible: boolean = false;
  renameSearchFormpopoverVisible: boolean = false;
  showValidationErrors: boolean = false; 

  validFeatureSystems: ValidFeatureSystem[] = [];
  featureDataSourceCache: Map<string, DataSource> = new Map();
  possibleConditions = [{
    key: 'and',
    text: this.translate.instant('und')
  }, {
    key: 'or',
    text: this.translate.instant('oder')
  }];
  
  constructor(
    public translate: TranslateService,
    public searchFormService: SearchFormsService,
    public loginService: LoginService,
    public updateTemplateService: UpdateTemplateService,
    public templateFieldsService: TemplateFieldsService,
    private validFeatureService: ValidFeatureService,
    private systemService: SystemService) {
  }

  async ngOnInit() {
    this.createNewSearchFormPopoverVisible = false;
    this.renameSearchFormpopoverVisible = false;
    this.groups = this.updateTemplateService.GetFieldGroups();
    await this.templateFieldsService.loadFields();
    this.allFields = this.templateFieldsService.translatedFields;
    this.validFeatureSystems = await lastValueFrom(
      this.validFeatureService.getSystems(this.loginService.currentUser.customerId, false)
    ) ?? [];
    await this.loadAndApplyInitialSelection();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['initialSearchForm'] && !changes['initialSearchForm'].firstChange) {
      this.loadAndApplyInitialSelection();
    }
  }

  private async loadAndApplyInitialSelection() {
    var allSearchForms = await this.searchFormService.getAll(this.loginService.currentUser.customerId);
    this.allSearchForms = allSearchForms ?? [];
    this.applyInitialSelection();
  }

  private async applyInitialSelection() {
    this.selectedListItem = (this.initialSearchForm && this.allSearchForms?.length)
      ? this.allSearchForms.find(sf => sf.id === this.initialSearchForm.id) ?? null
      : null;
    await this.loadWorkingCopy(this.selectedListItem);
  }

  private deepClone<T>(obj: T): T {
    return typeof structuredClone === 'function'
      ? structuredClone(obj)
      : JSON.parse(JSON.stringify(obj));
  }

  private async loadWorkingCopy(source: SearchForm | null) {
    this.selectedSearchForm = source ? this.deepClone(source) : null;
    this.showValidationErrors = false;
    if (this.selectedSearchForm?.searchOptions) {
      for (const option of this.selectedSearchForm.searchOptions) {
        this.migrateMultiValueOption(option);
        if (option.nextPimField?.systemKey) {
          await this.loadFeaturesForSystem(option.nextPimField.systemKey);
        }
      }
    }
  }

  private migrateMultiValueOption(option: SearchFormOptionItem) {
    if (!option?.operator || !this.isMultiValueOperator(option.operator))
      return;
    if (!option.values?.length && option.value != null && option.value !== '') {
      option.values = [String(option.value)];
    }
    option.value = null;
    option.valueTo = null;
  }

  async onListSelectionChanged(e: any) {
    if (!e.event)
      return;
    this.selectedListItem = e.value;
    await this.loadWorkingCopy(e.value);
  }

  removeRow(index: number) {
    this.selectedSearchForm.searchOptions.splice(index, 1);
    if(this.isFormValid())
      this.showValidationErrors = false;
  }

  addNewRow() {
    var currentMaxOrder = this.selectedSearchForm?.searchOptions?.length ?? 0;
    this.selectedSearchForm.searchOptions.push({
      order: currentMaxOrder + 1,
      nextPimField: new NextPimField()
    });
  }

  deleteSearchForm() {
    if (!this.selectedSearchForm)
      return;
    const target = this.selectedListItem;
    if (this.selectedSearchForm.id) {
      this.searchFormService.delete(this.selectedSearchForm, this.loginService.currentUser.customerId);
    }
    this.allSearchForms = this.allSearchForms.filter(x => x !== target);
    this.selectedListItem = null;
    this.selectedSearchForm = null;
    this.showValidationErrors = false;
  }

  copySearchForm() {
    if (!this.selectedSearchForm)
      return;
    const copiedSearchForm = this.deepClone(this.selectedSearchForm);
    copiedSearchForm.id = null;
    copiedSearchForm.name = (copiedSearchForm.name ?? "") + " " + this.translate.instant("Kopie");
    this.allSearchForms = [...this.allSearchForms, copiedSearchForm];
    this.selectedListItem = copiedSearchForm;
    this.selectedSearchForm = this.deepClone(copiedSearchForm);
  }

  createNewSearchForm() {
    const newSearchForm = new SearchForm();
    newSearchForm.name = this.translate.instant("Neues Suchformular");
    newSearchForm.userId = this.loginService.currentUser.id;
    newSearchForm.filterCondition = this.conditionValue ?? "and";
    newSearchForm.searchOptions = [];

    this.allSearchForms = [...this.allSearchForms, newSearchForm];
    this.selectedListItem = newSearchForm;
    this.selectedSearchForm = this.deepClone(newSearchForm);
    this.addNewRow();
  }

  private notifyLicenseError(error: unknown) {
    const key = error instanceof HttpErrorResponse && typeof error.error === 'string'
      ? error.error
      : 'Das Suchformular konnte nicht erstellt werden.';
    this.systemService.notifyWarning(this.translate.instant(key));
  }

  onReorderSearchOptions(event: any) {
    const options = this.selectedSearchForm?.searchOptions;
    if (!options) 
      return;

    const itemData = options.splice(event.fromIndex, 1)[0];
    if (itemData) {
      options.splice(event.toIndex, 0, itemData);
      options.forEach((item, index) => {
        if (item) item.order = index;
      });
    }
  }

  openNewRowPopup() {
    this.selectedSearchForm = new SearchForm();
    this.selectedSearchForm.userId = this.loginService.currentUser.id;
  }

  onSearchFormNameChanged(e: any) {
    this.selectedSearchForm.name = e.value;
    this.allSearchForms.find(x => x.id == this.selectedSearchForm.id).name = e.value;
  }

  getFieldsForGroup(group: string): TemplateItemTemplate[] {
    if (!group) return [];
    if (!this.groupFieldsCache.has(group)) {
      this.groupFieldsCache.set(group, this.allFields.filter(x => x.group === group));
    }
    return this.groupFieldsCache.get(group);
  }

  
  getFieldDisplayExpr(item: TemplateItemTemplate) {
    return item?.text === item?.field ? item?.field ?? '' : `${item.text} (${item.field})`;
  }

  private fieldToGroupCache: Map<string, string> = new Map();
  rowGroupSelection: Map<number, string> = new Map();

  getGroupForRow(row: SearchFormOptionItem): string {
    if (this.rowGroupSelection.has(row.order)) {
      return this.rowGroupSelection.get(row.order);
    }
    const fieldName = row.nextPimField?.field;
    if (!fieldName) 
      return null;
    if (!this.fieldToGroupCache.has(fieldName)) {
      const found = this.allFields.find(f => f.field === fieldName);
      this.fieldToGroupCache.set(fieldName, found?.group ?? null);
    }
    return this.fieldToGroupCache.get(fieldName);
  }

  onGroupChanged(e: any, row: SearchFormOptionItem) {
    if (!e.event) return;

    this.rowGroupSelection.set(row.order, e.value);
    if (row.nextPimField) {
      row.nextPimField.field = null;
      row.nextPimField.systemKey = null;
      row.nextPimField.elementKey = null;
    }
    row.operator = null;
    row.value = null;
    row.valueTo = null;
    row.values = null;
  }

  onFieldChanged(e: any, row: SearchFormOptionItem) {
    if (!e.event) return;
    this.rowGroupSelection.delete(row.order);
    row.dataType = this.isFeatureValueRow(row) ? this.getDataTypeForRow(row) : null;
    row.operator = null;
    row.value = null;
    row.valueTo = null;
    row.values = null;
  }

  onDataTypeChanged(e: any, row: SearchFormOptionItem) {
    if (!e.event) return;
    row.dataType = e.value;
    row.operator = null;
    row.value = null;
    row.valueTo = null;
    row.values = null;
  }

  private operatorsByDataType: Map<string, string[]> = new Map([
    ['string', ['equals', 'equals_not', 'one_of', 'none_of', 'contains', 'contains_not', 'begins_with', 'ends_with', 'is_empty', 'is_not_empty']],
    ['number', ['equals', 'equals_not', 'one_of', 'none_of', 'greater', 'greater_or_equal', 'less', 'less_or_equal', 'between', 'not_between', 'is_empty', 'is_not_empty']],
    ['date', ['at', 'before', 'after', 'between', 'is_empty', 'is_not_empty']],
    ['boolean', ['is_true', 'is_false']],
    ['enum', ['equals', 'equals_not', 'one_of', 'none_of', 'is_empty', 'is_not_empty']],
    ['multiEnum', ['is_in', 'is_not_in', 'contains_all', 'contains_any', 'is_empty', 'is_not_empty']],
  ]);

  private noValueOperators = new Set(['is_empty', 'is_not_empty', 'is_true', 'is_false']);
  private rangeOperators = new Set(['between', 'not_between']);
  private multiValueOperators = new Set(['equals_not', 'one_of', 'none_of']);
  featureDataTypes = ['string', 'number'];

  getOperatorsForField(fieldName: string, group: string): string[] {
    if (!fieldName || !group) return [];
    const dataType = this.getDataTypeForField(fieldName, group);
    return this.operatorsByDataType.get(dataType) ?? this.operatorsByDataType.get('string');
  }

  private operatorsForRowCache: Map<string, string[]> = new Map();

  getOperatorsForRow(row: SearchFormOptionItem): string[] {
    if (!row?.nextPimField?.field) return [];
    const dataType = this.getDataTypeForRow(row);
    const isFeature = this.isFeatureGroup(row);
    const cacheKey = `${dataType}|${isFeature}`;
    let operators = this.operatorsForRowCache.get(cacheKey);
    if (!operators) {
      const base = this.operatorsByDataType.get(dataType) ?? this.operatorsByDataType.get('string');
      const excludedOperators = isFeature ? ['equals_not'] : ['one_of', 'none_of'];
      operators = base.filter(op => !excludedOperators.includes(op));
      this.operatorsForRowCache.set(cacheKey, operators);
    }
    return operators;
  }

  translateGroup = (item: string): string => {
    return item ? this.translate.instant(item) : '';
  }

  translateOperator = (item: string): string => {
    return item ? this.translate.instant(item) : '';
  }

  translateDataType = (item: string): string => {
    if (!item) return '';
    const translationKey = item === 'number' ? 'Zahl' : 'Text';
    return this.translate.instant(translationKey);
  }

  getDataTypeForField(fieldName: string, group: string): string {
    if (!fieldName || !group) return 'string';
    const fields = this.getFieldsForGroup(group);
    const field = fields.find(f => f.field === fieldName);
    return field?.dataType ?? 'string';
  }

  getDataTypeForRow(row: SearchFormOptionItem): string {
    if (this.isFeatureValueRow(row) && row?.dataType) {
      return row.dataType;
    }
    return this.getDataTypeForField(row.nextPimField?.field, this.getGroupForRow(row));
  }

  getFeatureDataType(row: SearchFormOptionItem): string {
    if (!row.dataType) {
      row.dataType = this.getDataTypeForField(row.nextPimField?.field, this.getGroupForRow(row));
    }
    return row.dataType;
  }

  isNoValueOperator(operator: string): boolean {
    return this.noValueOperators.has(operator);
  }

  isRangeOperator(operator: string): boolean {
    return this.rangeOperators.has(operator);
  }

  isMultiValueOperator(operator: string): boolean {
    return this.multiValueOperators.has(operator);
  }

  onOperatorChanged(e: any, row: SearchFormOptionItem) {
    if (!e.event)
      return;
    const newOp = e.value;
    if (this.isNoValueOperator(newOp)) {
      row.value = null;
      row.valueTo = null;
      row.values = null;
    } else if (this.isMultiValueOperator(newOp)) {
      row.value = null;
      row.valueTo = null;
      row.values = row.values ?? [];
    } else if (this.isRangeOperator(newOp)) {
      row.values = null;
    } else {
      row.valueTo = null;
      row.values = null;
    }
  }

  isFormValid(): boolean {
    if (!this.selectedSearchForm?.searchOptions?.length) 
      return true;
    return this.selectedSearchForm.searchOptions.every(row => this.isRowValid(row));
  }

  isRowValid(row: SearchFormOptionItem): boolean {
    if (!row.nextPimField?.field || !row.operator)
      return false;
    if (this.isNoValueOperator(row.operator))
      return true;

    return true;
  }

  isFeatureGroup(row: SearchFormOptionItem): boolean {
    return this.getGroupForRow(row) === 'Features';
  }

  isFeatureValueRow(row: SearchFormOptionItem): boolean {
    return this.isFeatureGroup(row) && row.nextPimField?.field === 'FVALUE';
  }

  needsSystem(row: SearchFormOptionItem): boolean {
    if (!row.nextPimField?.field || !this.isFeatureGroup(row)) return false;
    const fields = this.getFieldsForGroup('Features');
    const field = fields.find(f => f.field === row.nextPimField.field);
    return field?.system === true;
  }

  needsElement(row: SearchFormOptionItem): boolean {
    if (!row.nextPimField?.field || !this.isFeatureGroup(row)) return false;
    const fields = this.getFieldsForGroup('Features');
    const field = fields.find(f => f.field === row.nextPimField.field);
    return field?.element === true;
  }

  async onFeatureSystemChanged(row: SearchFormOptionItem) {
    row.nextPimField.elementKey = null;
    await this.loadFeaturesForSystem(row.nextPimField.systemKey);
  }

  async loadFeaturesForSystem(systemId: string) {
    if (!systemId || this.featureDataSourceCache.has(systemId)) 
      return;
    const features = await lastValueFrom(
      this.validFeatureService.getFeatures(systemId, this.loginService.currentUser.customerId, false)
    ) ?? [];
    this.featureDataSourceCache.set(systemId, new DataSource({
      store: new ArrayStore({ key: 'id', data: features }),
      paginate: true,
      pageSize: 20
    }));
  }

  getFeaturesDataSource(systemId: string): DataSource {
    return this.featureDataSourceCache.get(systemId) ?? null;
  }

  featureDisplayExpr = (item: any): string => {
    if (!item) return '';
    return item.unit ? `${item.name} => ${item.unit}` : item.name;
  };

  async saveSearchForm(): Promise<boolean> {
    if (!this.isFormValid())
      return false;
    if (this.selectedSearchForm?.searchOptions) {
      for (const row of this.selectedSearchForm.searchOptions) {
        if (!this.isFeatureValueRow(row)) {
          row.dataType = null;
        }
        if (row.value != null && typeof row.value !== 'string') {
          row.value = row.value instanceof Date
            ? row.value.toISOString()
            : String(row.value);
        }
        if (row.valueTo != null && typeof row.valueTo !== 'string') {
          row.valueTo = row.valueTo instanceof Date
            ? row.valueTo.toISOString()
            : String(row.valueTo);
        }
        if (row.values?.length) {
          row.values = row.values
            .filter(v => v != null)
            .map(v => String(v));
        }
      }
    }
    try {
      const saved = await this.searchFormService.updateOrCreateSearchForm(this.selectedSearchForm, this.loginService.currentUser.customerId);
      this.selectedSearchForm.id = saved.id;
      this.upsertSavedIntoList(saved);
    } catch (error) {
      this.notifyLicenseError(error);
      return false;
    }
    this.showValidationErrors = false;
    return true;
  }

  private upsertSavedIntoList(saved: SearchForm) {
    const idx = this.selectedListItem ? this.allSearchForms.indexOf(this.selectedListItem) : -1;
    if (idx > -1) {
      this.allSearchForms[idx] = saved;
    } else {
      this.allSearchForms.push(saved);
    }
    this.allSearchForms = [...this.allSearchForms];
    this.selectedListItem = saved;
  }

  async onSave() {
    if (!this.selectedSearchForm)
      return;
    const wasSaved = await this.saveSearchForm();
    if (!wasSaved) {
      this.showValidationErrors = true;
      return;
    }
    this.saved.emit(this.selectedSearchForm);
  }

  onCancel() {
    this.allSearchForms = this.allSearchForms.filter(f => f.id);
    this.selectedListItem = null;
    this.selectedSearchForm = null;
    this.showValidationErrors = false;
    this.cancelled.emit();
  }

  getValidationErrors(): string[] {
    if (!this.selectedSearchForm?.searchOptions?.length) 
      return [];
    const errors: string[] = [];
    this.selectedSearchForm.searchOptions.forEach((row, i) => {
      if (!row.nextPimField?.field) {
        errors.push(this.translate.instant('Zeile') + ` ${i + 1}: ` + this.translate.instant('Kein Feld ausgewählt'));
      } else if (!row.operator) {
        errors.push(this.translate.instant('Zeile') + ` ${i + 1}: ` + this.translate.instant('Kein Operator ausgewählt'));
      } else if (!this.isNoValueOperator(row.operator)) {
        if (this.isRangeOperator(row.operator)) {
          if (row.value == null || row.value === '' || row.valueTo == null || row.valueTo === '') {
            errors.push(this.translate.instant('Zeile') + ` ${i + 1}: ` + this.translate.instant('Von- und Bis-Wert erforderlich'));
          }
        } 
      }
    });
    return errors;
  }

}
